import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faArrowLeft,
  faDownload,
  faCheckCircle,
  faClock,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface SubmissionItem {
  submissionId: string;
  studentId: string;
  studentUserId: string;
  studentName: string;
  studentIdNumber: string;
  submittedAt: string | null;
  fileUrl: string | null;
  fileName: string | null;
  submissionNote: string | null;
  isLate: boolean;
  status: number;
  grade: number | null;
  feedback: string | null;
  gradedAt: string | null;
  resubmissionAllowed: boolean;
  moderationOutcome?: number | null;
  moderationComment?: string | null;
}

interface AssignmentDetail {
  assignmentId: string;
  learnershipTitle: string;
  title: string;
  instructions: string;
  dueDate: string;
  maxMark: number;
  passingMark: number;
  isPublished: boolean;
  submissions: SubmissionItem[];
}

const statusLabels: { [key: number]: { label: string; className: string } } = {
  0: { label: 'Not submitted', className: 'bg-gray-100 text-gray-600' },
  1: { label: 'Submitted', className: 'bg-yellow-100 text-yellow-800' },
  2: { label: 'Graded', className: 'bg-green-100 text-green-700' },
  3: { label: 'Resubmission requested', className: 'bg-orange-100 text-orange-700' },
};

export default function InstructorAssignmentGradingPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [assignment, setAssignment] = useState<AssignmentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [gradingSubmission, setGradingSubmission] = useState<SubmissionItem | null>(null);
  const [gradeValue, setGradeValue] = useState('');
  const [feedbackValue, setFeedbackValue] = useState('');
  const [allowResubmission, setAllowResubmission] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [gradeError, setGradeError] = useState('');

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const fetchDetail = async () => {
    if (!assignmentId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`/api/assignments/${assignmentId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to load assignment');
      setAssignment(await response.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load assignment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openGradeModal = (submission: SubmissionItem) => {
    setGradingSubmission(submission);
    setGradeValue(submission.grade?.toString() ?? '');
    setFeedbackValue(submission.feedback ?? '');
    setAllowResubmission(submission.resubmissionAllowed);
    setGradeError('');
  };

  const handleGrade = async () => {
    if (!gradingSubmission || !assignmentId) return;
    const gradeNum = parseInt(gradeValue, 10);
    if (isNaN(gradeNum) || gradeNum < 0 || (assignment && gradeNum > assignment.maxMark)) {
      setGradeError(`Grade must be between 0 and ${assignment?.maxMark ?? 100}.`);
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch(
        `/api/assignments/${assignmentId}/submissions/${gradingSubmission.submissionId}/grade`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ grade: gradeNum, feedback: feedbackValue, allowResubmission }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        setGradeError(data.message || 'Failed to save grade.');
        return;
      }

      setGradingSubmission(null);
      await fetchDetail();
    } catch (err) {
      console.error(err);
      setGradeError('An error occurred while saving the grade.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleString('en-ZA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 mb-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-brand-950">{assignment?.title || 'Assignment'}</h1>
        {assignment && <p className="text-sm text-brand-900/60 mt-1">{assignment.learnershipTitle}</p>}
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && assignment && (
          <>
            <div className="card-surface p-6 mb-6">
              <p className="text-sm text-brand-900/70 whitespace-pre-wrap">{assignment.instructions || 'No instructions provided.'}</p>
              <div className="flex flex-wrap gap-4 mt-4 text-xs text-brand-900/60">
                <span>Due: {formatDateTime(assignment.dueDate)}</span>
                <span>Max mark: {assignment.maxMark}</span>
                <span>Passing mark: {assignment.passingMark}</span>
              </div>
            </div>

            <div className="card-surface overflow-hidden">
              <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-canvas-50 text-xs font-bold uppercase tracking-wider text-brand-900/50">
                <span>Student</span>
                <span>Submitted</span>
                <span>Status</span>
                <span>Grade</span>
                <span></span>
              </div>
              <div className="divide-y divide-canvas-200">
                {assignment.submissions.map((s) => {
                  const status = statusLabels[s.status] ?? statusLabels[0];
                  return (
                    <div
                      key={s.studentId}
                      className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 md:gap-4 px-5 py-4 items-center"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-950 truncate">{s.studentName}</p>
                        <p className="text-xs text-brand-900/50 truncate">{s.studentIdNumber}</p>
                      </div>
                      <div className="text-sm text-brand-900/70">
                        {formatDateTime(s.submittedAt)}
                        {s.isLate && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-red-600">
                            <FontAwesomeIcon icon={faClock} className="h-3 w-3" /> Late
                          </span>
                        )}
                      </div>
                      <span className={`inline-block w-fit px-2.5 py-1 rounded-full text-xs font-semibold ${status.className}`}>
                        {status.label}
                      </span>
                      <p className="text-sm font-semibold text-brand-950">
                        {s.grade != null ? `${s.grade}/${assignment.maxMark}` : '—'}
                      </p>
                      <div className="flex gap-2">
                        {s.fileUrl && (
                          <a
                            href={s.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                          >
                            <FontAwesomeIcon icon={faDownload} className="h-3 w-3" />
                          </a>
                        )}
                        {s.status !== 0 && (
                          <button
                            onClick={() => openGradeModal(s)}
                            className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all"
                          >
                            {s.grade != null ? 'Edit grade' : 'Grade'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {gradingSubmission && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">Grade {gradingSubmission.studentName}</h3>
              <button onClick={() => setGradingSubmission(null)} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            {gradingSubmission.submissionNote && (
              <div className="mb-4 p-3 bg-canvas-50 rounded-lg text-sm text-brand-900/70">
                <span className="font-semibold">Student note: </span>
                {gradingSubmission.submissionNote}
              </div>
            )}

            {gradingSubmission.moderationOutcome != null && (
              <div className="mb-4 p-3 bg-brand-50 border border-brand-100 rounded-lg text-sm text-brand-900/80">
                <span className="font-semibold">Moderator ({['Agreed', 'Disagreed', 'Referred back'][gradingSubmission.moderationOutcome]}): </span>
                {gradingSubmission.moderationComment}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">
                  Grade (out of {assignment?.maxMark ?? 100}) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={gradeValue}
                  onChange={(e) => setGradeValue(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Feedback</label>
                <textarea
                  value={feedbackValue}
                  onChange={(e) => setFeedbackValue(e.target.value)}
                  rows={4}
                  placeholder="Feedback for the student..."
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-brand-900/80">
                <input
                  type="checkbox"
                  checked={allowResubmission}
                  onChange={(e) => setAllowResubmission(e.target.checked)}
                  className="rounded border-canvas-300"
                />
                Allow this student to resubmit
              </label>

              {gradeError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                  <p className="text-sm text-red-700">{gradeError}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setGradingSubmission(null)}
                className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleGrade}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isSaving ? faSpinner : faCheckCircle} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                {isSaving ? 'Saving...' : 'Save Grade'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
